import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  getInvoice,
  markInvoicePaid,
  voidInvoice,
  type PublicInvoice,
} from '../../lib/invoices-api';
import { extractApiError } from '../../lib/api-client';
import { formatDate, formatDateTime, formatRupiah } from '../../lib/format';
import { InvoiceStatusChip } from './status-chip';
import { useT } from '../../i18n';

export function InvoiceShowPage() {
  const { id } = useParams<{ id: string }>();
  const t = useT();
  const [invoice, setInvoice] = useState<PublicInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  async function refresh() {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const inv = await getInvoice(id);
      setInvoice(inv);
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !invoice) {
    return (
      <Stack spacing={2}>
        <Button startIcon={<ArrowBackIcon />} component={RouterLink} to="/invoices">
          {t('inv.backToList')}
        </Button>
        <Alert severity="error">{error ?? t('inv.notFound')}</Alert>
      </Stack>
    );
  }

  const canMarkPaid = invoice.status !== 'PAID' && invoice.status !== 'VOID';
  const canVoid = invoice.status !== 'VOID' && invoice.status !== 'PAID';
  const outstandingCents = Math.max(invoice.total_cents - invoice.paid_cents, 0);

  return (
    <Stack spacing={3}>
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to="/invoices"
        sx={{ alignSelf: 'flex-start' }}
      >
        {t('inv.backToList')}
      </Button>

      {flash ? <Alert severity="success" onClose={() => setFlash(null)}>{flash}</Alert> : null}

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <div>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h4" fontWeight={600}>
              {invoice.number}
            </Typography>
            <InvoiceStatusChip status={invoice.status} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('inv.sourceLabel', { source: invoice.source })}
          </Typography>
        </div>
        <Stack direction="row" spacing={1}>
          {canMarkPaid ? (
            <Button variant="contained" onClick={() => setMarkPaidOpen(true)}>
              {t('inv.markPaid')}
            </Button>
          ) : null}
          {canVoid ? (
            <Button variant="outlined" color="error" onClick={() => setVoidOpen(true)}>
              {t('inv.void')}
            </Button>
          ) : null}
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                {t('inv.detailsSection')}
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <DetailRow label={t('inv.unitId')} value={invoice.unit_id} mono />
                <DetailRow label={t('inv.colIssued')} value={formatDate(invoice.issue_date)} />
                <DetailRow label={t('inv.colDue')} value={formatDate(invoice.due_date)} />
                <DetailRow label={t('inv.paidAt')} value={formatDateTime(invoice.paid_at)} />
                {invoice.voided_at ? (
                  <>
                    <DetailRow label={t('inv.voidedAt')} value={formatDateTime(invoice.voided_at)} />
                    <DetailRow label={t('inv.voidReason')} value={invoice.voided_reason ?? '—'} />
                  </>
                ) : null}
                {invoice.notes ? <DetailRow label={t('inv.notes')} value={invoice.notes} /> : null}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                {t('inv.amountsSection')}
              </Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                <AmountRow label={t('inv.subtotal')} cents={invoice.subtotal_cents} />
                <AmountRow label={t('inv.tax')} cents={invoice.tax_cents} />
                <Divider />
                <AmountRow label={t('inv.total')} cents={invoice.total_cents} bold />
                <AmountRow label={t('inv.paid')} cents={invoice.paid_cents} />
                {outstandingCents > 0 ? (
                  <AmountRow label={t('inv.outstanding')} cents={outstandingCents} bold />
                ) : null}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card variant="outlined">
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="overline" color="text.secondary">
              {t('inv.lineItems')}
            </Typography>
          </Box>
          <Divider />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('inv.colDescription')}</TableCell>
                  <TableCell align="right" sx={{ width: 80 }}>
                    {t('inv.colQty')}
                  </TableCell>
                  <TableCell align="right" sx={{ width: 140 }}>
                    {t('inv.colUnitPrice')}
                  </TableCell>
                  <TableCell align="right" sx={{ width: 140 }}>
                    {t('inv.colTotal')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(invoice.items ?? []).map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.description}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">{formatRupiah(item.unit_price_cents)}</TableCell>
                    <TableCell align="right">{formatRupiah(item.total_cents)}</TableCell>
                  </TableRow>
                ))}
                {(invoice.items ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                      {t('inv.noLineItems')}
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <MarkPaidDialog
        open={markPaidOpen}
        outstandingCents={outstandingCents > 0 ? outstandingCents : invoice.total_cents}
        onClose={() => setMarkPaidOpen(false)}
        onConfirmed={async (msg) => {
          setMarkPaidOpen(false);
          setFlash(msg);
          await refresh();
        }}
        invoiceId={invoice.id}
      />
      <VoidDialog
        open={voidOpen}
        onClose={() => setVoidOpen(false)}
        onConfirmed={async (msg) => {
          setVoidOpen(false);
          setFlash(msg);
          await refresh();
        }}
        invoiceId={invoice.id}
      />
    </Stack>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <Grid size={4}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Grid>
      <Grid size={8}>
        <Typography
          variant="body2"
          sx={mono ? { fontFamily: 'monospace', wordBreak: 'break-all' } : undefined}
        >
          {value}
        </Typography>
      </Grid>
    </>
  );
}

function AmountRow({ label, cents, bold }: { label: string; cents: number; bold?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" fontWeight={bold ? 600 : 400}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={bold ? 600 : 400}>
        {formatRupiah(cents)}
      </Typography>
    </Stack>
  );
}

// ─── Mark-paid dialog ────────────────────────────────────────────────────────
function MarkPaidDialog(props: {
  open: boolean;
  outstandingCents: number;
  invoiceId: string;
  onClose: () => void;
  onConfirmed: (message: string) => Promise<void>;
}) {
  const { open, outstandingCents, invoiceId, onClose, onConfirmed } = props;
  const t = useT();
  const [method, setMethod] = useState<'MANUAL_TRANSFER' | 'CASH'>('MANUAL_TRANSFER');
  const defaultRupiah = String(Math.floor(outstandingCents / 100));
  const [amountRupiah, setAmountRupiah] = useState(defaultRupiah);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset form whenever dialog reopens with a different outstanding.
  useEffect(() => {
    if (open) {
      setMethod('MANUAL_TRANSFER');
      setAmountRupiah(defaultRupiah);
      setNote('');
      setErr(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function handleSubmit() {
    const rupiah = Number(amountRupiah);
    if (!Number.isFinite(rupiah) || rupiah <= 0) {
      setErr(t('inv.amountPositive'));
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const out = await markInvoicePaid(invoiceId, {
        method,
        amount_cents: Math.round(rupiah * 100),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      await onConfirmed(
        t('inv.recordedPaid', { amount: formatRupiah(out.invoice.paid_cents) }),
      );
    } catch (e) {
      setErr(extractApiError(e).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('inv.markPaidTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {err ? <Alert severity="error">{err}</Alert> : null}
          <FormControl>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('inv.paymentMethod')}
            </Typography>
            <RadioGroup
              row
              value={method}
              onChange={(e) => setMethod(e.target.value as 'MANUAL_TRANSFER' | 'CASH')}
            >
              <FormControlLabel
                value="MANUAL_TRANSFER"
                control={<Radio />}
                label={t('inv.bankTransfer')}
              />
              <FormControlLabel value="CASH" control={<Radio />} label={t('inv.cash')} />
            </RadioGroup>
          </FormControl>
          <TextField
            label={t('inv.amount')}
            type="number"
            value={amountRupiah}
            onChange={(e) => setAmountRupiah(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">Rp</InputAdornment>,
            }}
            helperText={
              outstandingCents > 0
                ? t('inv.outstandingAmount', { amount: formatRupiah(outstandingCents) })
                : undefined
            }
            fullWidth
          />
          <TextField
            label={t('inv.noteOptional')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            placeholder={t('inv.notePlaceholder')}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? t('common.saving') : t('inv.confirmPayment')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Void dialog ─────────────────────────────────────────────────────────────
function VoidDialog(props: {
  open: boolean;
  invoiceId: string;
  onClose: () => void;
  onConfirmed: (message: string) => Promise<void>;
}) {
  const { open, invoiceId, onClose, onConfirmed } = props;
  const t = useT();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setErr(null);
    }
  }, [open]);

  async function handleSubmit() {
    const trimmed = reason.trim();
    if (trimmed.length < 1) {
      setErr(t('inv.reasonRequired'));
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      await voidInvoice(invoiceId, trimmed);
      await onConfirmed(t('inv.invoiceVoided'));
    } catch (e) {
      setErr(extractApiError(e).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('inv.voidTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {err ? <Alert severity="error">{err}</Alert> : null}
          <Alert severity="warning">
            {t('inv.voidWarning')}
          </Alert>
          <TextField
            label={t('inv.reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            placeholder={t('inv.reasonPlaceholder')}
            fullWidth
            autoFocus
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="error" disabled={submitting}>
          {submitting ? t('inv.voiding') : t('inv.voidTitle')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
